import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { signOut, useTranslation, useAuthSession } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { Button } from '@/components/ui/button';

interface AuthGuardProps {
  /** 'require-store': render access state if no active membership.
   *  'require-no-store': redirect to /dashboard if has store (for /create-store route). */
  mode: 'require-store' | 'require-no-store';
  children: ReactNode;
}

/**
 * Route protection component.
 *
 * - Not authenticated -> redirect to /auth/signin
 * - mode='require-store' + no active membership -> access-pending/unauthorized state
 * - mode='require-no-store' + has store -> redirect to /dashboard
 * - Otherwise -> render children
 *
 * Uses render-phase <Navigate replace /> (no useEffect) to avoid
 * content flicker, matching the pattern established in SignupRoute.
 */
export function AuthGuard({ mode, children }: AuthGuardProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const authSession = useAuthSession(supabase);
  const { storeId, accessState, merchantStatus, isLoading, isError, refetch } =
    useCurrentStore(authSession);

  // Still loading session or store membership
  if (isLoading) {
    return (
      <div
        data-testid="auth-guard-loading"
        className="bg-background flex min-h-screen items-center justify-center"
      >
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  // Not authenticated
  if (!authSession.session) {
    return <Navigate to="/auth/signin" state={{ from: location.pathname }} replace />;
  }

  // Store membership query failed
  if (isError) {
    return (
      <div
        data-testid="auth-guard-error"
        className="bg-background flex min-h-screen items-center justify-center px-4"
      >
        <div className="bg-card border-border rounded-xl border p-6 text-center shadow-sm">
          <p className="text-foreground">{t('errors.networkFailed')}</p>
          <Button type="button" variant="outline" className="mt-4" onClick={refetch}>
            {t('common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (mode === 'require-store' && !storeId) {
    if (merchantStatus === 'approved') {
      return <Navigate to="/create-store" replace />;
    }
    return <MerchantAccessState state={accessState ?? 'pending'} />;
  }

  if (mode === 'require-no-store' && !storeId) {
    if (merchantStatus === 'approved') {
      return <>{children}</>;
    }
    return <MerchantAccessState state={accessState ?? 'pending'} />;
  }

  if (mode === 'require-no-store' && storeId) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function MerchantAccessState({ state }: { state: 'pending' | 'unauthorized' | 'member' }) {
  const { t } = useTranslation();
  const isPending = state === 'pending';
  const handleSignOut = (): void => {
    void signOut(supabase).finally(() => {
      window.location.assign('/auth/signin');
    });
  };

  return (
    <main
      data-testid={isPending ? 'merchant-pending-state' : 'merchant-missing-state'}
      className="bg-background flex min-h-screen items-center justify-center px-4"
    >
      <section className="bg-card border-border max-w-md rounded-xl border p-6 text-center shadow-sm">
        <h1 className="font-display text-foreground text-2xl">
          {isPending
            ? t('admin.authBoundary.pendingTitle')
            : t('admin.authBoundary.unauthorizedTitle')}
        </h1>
        <p className="text-muted-foreground mt-3 text-sm leading-6">
          {isPending
            ? t('admin.authBoundary.pendingBody')
            : t('admin.authBoundary.unauthorizedBody')}
        </p>
        <Button type="button" variant="outline" className="mt-5 w-full" onClick={handleSignOut}>
          {t('admin.authBoundary.signOut')}
        </Button>
      </section>
    </main>
  );
}
